import { useCallback, useState } from "react"
import {
  cleanDraftPlannerSettings,
  plannerSettingsEqual,
} from "@/features/voyagePlanner/voyageSettingsFields"

// Owns the draft-copy lifecycle of the specification tabs: copy the committed
// settings into the draft on entering the specs view, collect per-field
// validation flags, and validate-and-commit on leaving. Shared by the voyage
// planner wizard's Options step and the RNW analyser's specifications view.
export default function useSpecificationsDraft({
  plannerVesselSettings,
  setPlannerVesselSettings,
  draftPlannerVesselSettings,
  setDraftPlannerVesselSettings,
}) {
  const [optionsErrors, setOptionsErrors] = useState({})
  const setOptionsError = useCallback((key, value) => {
    setOptionsErrors((prev) =>
      Boolean(prev[key]) === Boolean(value)
        ? prev
        : { ...prev, [key]: Boolean(value) },
    )
  }, [])
  const hasOptionsErrors = Object.values(optionsErrors).some(Boolean)

  // Copy the committed settings into the editable draft (on specs entry).
  const enterSpecs = useCallback(() => {
    setDraftPlannerVesselSettings(
      JSON.parse(JSON.stringify(plannerVesselSettings)),
    )
    setOptionsErrors({})
  }, [plannerVesselSettings, setDraftPlannerVesselSettings])

  // Validate + commit the draft. Only commits real changes — a fresh object
  // reference would needlessly trigger draft invalidation. Returns the
  // committed settings, or null when blocked by validation errors.
  const commitIfValid = () => {
    if (hasOptionsErrors) return null
    const next = cleanDraftPlannerSettings(draftPlannerVesselSettings)
    if (!plannerSettingsEqual(next, plannerVesselSettings)) {
      setPlannerVesselSettings(next)
    }
    return next
  }

  return {
    optionsErrors,
    setOptionsError,
    hasOptionsErrors,
    enterSpecs,
    commitIfValid,
  }
}
